import {view, css, Spinner} from '@sail/ui/deprecated';

import {useEffect, useState} from 'react';
import {defineMessages, useIntl, FormattedMessage} from 'react-intl';

import {
  useNewUniversalHelpDesign,
  useUniversalHelpDotComDesign,
  useDotComFeedbackButtons,
  BlinkingCursor,
} from '@stripe-internal/conversations-ui';
import {WordStreamer} from './WordStreamer';
import {LiveAnswersSource} from './UniversalHelpLiveAnswersResponse';
import {SourcesDropdown} from './sources/SourcesDropdown';
import {DotComSourcesDropdown} from './sources/DotComSourcesDropdown';
import {Feedback} from './Feedback';
import {ResponseBubble} from './ResponseBubble';
import {ResponseMessageNext} from './ResponseMessageNext';

const messages = defineMessages({
  unanswerable: {
    id: 'mse_inline_data.universal_help_live_answers_response.response_content.unanswerable',
    description:
      'Response text shown to users when their question cannot be answered by the support ai',
    defaultMessage: "Sorry, I wasn't able to answer your question.",
  },
  agent_escalation: {
    id: 'mse_inline_data.universal_help_live_answers_response.response_content.agent_escalation',
    description:
      'Response text shown to users when their question is asking for a human agent',
    defaultMessage: "It looks like you're trying to reach an agent.",
  },
  streaming_failed: {
    id: 'mse_inline_data.universal_help_live_answers_response.response_content.streaming_failed',
    description:
      'Response text shown to users when an error occurs returning a response from support ai',
    defaultMessage:
      "Sorry, an error occurred while processing your question. I'll direct you towards other resources for help.",
  },
  loading: {
    id: 'mse_inline_data.universal_help_live_answers_response.response_content.loading',
    description:
      'Message shown to users next to a loading spinner when we are still waiting for the first chunk of response text',
    defaultMessage: 'Building answer from all relevant documentation',
  },
});

export type ResponseContentProps = {
  response?: string;
  sources?: Array<LiveAnswersSource>;
  unanswerable: boolean;
  agentEscalation?: boolean;
  isFailure?: boolean;
  isComplete?: boolean;
  isDone?: boolean;
  streamingConversationId?: string;
  setRenderingComplete: (renderingComplete: boolean) => void;
};

export const ResponseContent = (props: ResponseContentProps) => {
  const {
    response,
    sources,
    unanswerable,
    agentEscalation,
    isFailure,
    isComplete,
    isDone,
    streamingConversationId,
    setRenderingComplete,
  } = props;

  const useNewDesign = useNewUniversalHelpDesign();
  const feedbackButtons = useDotComFeedbackButtons();
  const useDotComDesign = useUniversalHelpDotComDesign();

  const intl = useIntl();
  const [finishedStreaming, setFinishedStreaming] = useState(false);

  let text = response;
  if (isFailure) {
    text = intl.formatMessage(messages.streaming_failed);
  } else if (agentEscalation) {
    text = intl.formatMessage(messages.agent_escalation);
  } else if (unanswerable) {
    text = intl.formatMessage(messages.unanswerable);
  }

  useEffect(() => {
    setRenderingComplete(finishedStreaming);
  }, [finishedStreaming, setRenderingComplete]);

  if (!text) {
    if (isDone) {
      // When there's no text to display but isDone is set, which means that the Live Answers inline data is not the most active
      // event in the conversation, we should not return any content since we reached an error state and have either shown an error
      // message or fallen back on article suggester, depending on the UH tree configuration.
      // The UniversalHelpLiveAnswersResponse component checks the return type of this component to additionally hide the avatar.
      return null;
    }

    if (useNewDesign || useDotComDesign) {
      return <BlinkingCursor />;
    }

    return (
      <view.div
        css={{
          stack: 'x',
          gap: 'xsmall',
          alignX: 'start',
          fontSize: css.value('12px'),
        }}
        data-testid="live-answers-loading-spinner"
      >
        <Spinner size="small" />
        <FormattedMessage {...messages.loading} />
      </view.div>
    );
  }

  const hasDotComFeedbackButtons = useDotComDesign && feedbackButtons;

  return (
    <WordStreamer
      text={text}
      // Failure and agent-escalation paths do not stream text as we imitate
      // a standard Stripe avatar (and not the AI-specific one).
      // The Unanswerable path uses Support AI branding but also doesn't stream.
      // We don't stream when we've already completed rendering as well,
      // to avoid re-streaming the text on the client on re-renders.
      textComplete={isComplete || isFailure || agentEscalation || unanswerable}
      delayMs={40}
      onFinished={() => setFinishedStreaming(true)}
    >
      {(outputText: string) => {
        const rows = outputText
          .split('\n')
          .map((line) => line.trim())
          .filter((line) => line.length > 0)
          .map((line, i) =>
            useNewDesign ? (
              // eslint-disable-next-line react/no-array-index-key
              <ResponseMessageNext text={line} key={i} />
            ) : (
              // eslint-disable-next-line react/no-array-index-key
              <ResponseBubble text={line} key={`live-answers-response-${i}`} />
            ),
          );

        let containerGap: 'xsmall' | 'medium' | 'small' = 'xsmall';

        if (useNewDesign) {
          containerGap = 'medium';
        } else if (useDotComDesign) {
          containerGap = 'small';
        }

        return (
          <view.div
            css={{
              stack: 'y',
              gap: containerGap,
              width: css.value('100%'),
            }}
          >
            {rows}
            {finishedStreaming && !isFailure && !unanswerable && sources ? (
              <view.div
                css={{
                  stack: 'x',
                  gap: 'small',
                  width: hasDotComFeedbackButtons
                    ? css.value('100%')
                    : css.value('88%'),
                }}
              >
                <view.div
                  css={{
                    width: hasDotComFeedbackButtons
                      ? css.value('calc(100% - 60px)')
                      : css.value('100%'),
                  }}
                >
                  {useDotComDesign ? (
                    <DotComSourcesDropdown sources={sources} />
                  ) : (
                    <SourcesDropdown sources={sources} />
                  )}
                </view.div>
                {hasDotComFeedbackButtons ? (
                  <Feedback streamingConversationId={streamingConversationId} />
                ) : null}
              </view.div>
            ) : null}
          </view.div>
        );
      }}
    </WordStreamer>
  );
};
